import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import BottomBar from "@saleor/alternatedMaterial/components/BottomBar";
import DialogSuccess from "@saleor/alternatedMaterial/components/Common/DialogSuccess";
import Container from "@saleor/components/Container";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  AlterMaterialFilterInput,
  useAlternativeMaterialsOsLazyQuery,
  useDeleteAlternativeMaterialMutation,
  useExportAlternativeMaterialMutation
} from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import AlternatedMaterialFilter from "../../components/AlternatedMaterialFilter";
import AlternatedMaterialTable from "../../components/AlternatedMaterialTable";
import UploadFile from "../../components/UploadFile";
import { AltMatListUrl, AltMatUrlQueryParams } from "../../urls";
import { getSortQueryAlternateMaterialVariables } from "./sort";

interface AltMatProps {
  params: AltMatUrlQueryParams;
}

const useStyles = makeStyles(
  theme => ({
    boxContainer: {
      paddingBottom: "6rem"
    },
    filterUploadBox: {
      display: "grid",
      gap: "30px",
      gridTemplateColumns: "60% auto",
      [theme.breakpoints.down("md")]: {
        flexDirection: "column"
      }
    },
    dialog: {
      width: "420px",
      height: "242px",
      padding: "32px 24px 32px 24px",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
      borderRadius: "16px"
    },
    titles: {
      fontSize: "20px",
      fontWeight: 700
    },
    button: {
      alignSelf: "center",
      backgroundColor: "#009FB8",
      borderRadius: "4px",
      width: "315px",
      height: "48px",
      marginTop: "50px"
    },
    buttonTitle: {
      fontSize: "18px",
      color: "white"
    },
    priorityWrapper: {
      position: "relative",
      backgroundColor: "#F4F7FE",
      padding: "8px 16px",
      paddingRight: 100,
      borderRadius: 8,
      width: "100%",
      marginBottom: 16,
      "&:hover": {
        border: "1px solid #009FB8",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)"
      }
    },
    priorityHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    },
    removeButton: {
      padding: "10px",
      cursor: "pointer",
      "& img": {
        height: "15px"
      }
    },
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        margin: 0,
        marginBottom: "1.2rem"
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    snackHeadTitle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "1.2rem"
    },
    snackBarWrapper: {
      width: 780
    }
  }),
  { name: "AlternatedMaterialView" }
);

const AlternatedMaterialView: React.FC<AltMatProps> = ({ params }) => {
  const classes = useStyles();
  const navigate = useNavigator();

  const intl = useIntl();
  const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);

  const [visible, setVisible] = useState<boolean>(false);
  const [disableButton, setdisableButton] = useState<boolean>(false);
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [
    getDataAlternativeMaterialsOs,
    {
      data: fetchDataAlternativeMaterialsOs,
      loading: loadingDataAlternativeMaterialsOs,
      refetch
    }
  ] = useAlternativeMaterialsOsLazyQuery({
    fetchPolicy: "no-cache"
  });

  const [deleteAlternativeMaterial] = useDeleteAlternativeMaterialMutation();

  const [exportAlternativeMaterial] = useExportAlternativeMaterialMutation();

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
  };
  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs.pageInfo,
    valuesPaginationState
  );

  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.FILTER_ORDER.rowNumber);
  }, []);
  const queryVariables = {
    sortBy: getSortQueryAlternateMaterialVariables(params),
    ...valuesPaginationState
  };

  useEffect(() => {
    const { before, first, last, after } = valuesPaginationState;
    if (refetch) {
      setdisableButton(true);
      refetch({
        ...queryVariables,
        before,
        first,
        last,
        after
      }).then(() => {
        setdisableButton(false);
      });
    }
  }, [settings?.rowNumber, valuesPaginationState]);
  useEffect(() => {
    getDataAlternativeMaterialsOs({
      variables: queryVariables
    });
  }, []);
  const handleSearch = (value: AlterMaterialFilterInput) => {
    setIsSearched(true);
    getDataAlternativeMaterialsOs({
      variables: {
        ...queryVariables,
        filter: value
      }
    });
  };

  const listAlternativeMaterialsOs =
    useMemo(
      () =>
        mapEdgesToItems(
          fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs
        ),
      [fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs]
    ) || [];

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const lastPaginationState = () => {
    setValuesPaginationState({
      last:
        fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs
          .latestPageItemNumber
    });
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  const handleDownloadFile = async () => exportAlternativeMaterial();

  const message = useMemo(() => {
    if (loadingDataAlternativeMaterialsOs) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (!isSearched) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (
      isSearched &&
      !fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs.totalCount
    ) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    return {
      icon: CheckCircle,
      message: intl.formatMessage({
        defaultMessage: "พบรายการที่เกี่ยวข้อง"
      }),
      color: "#28A745"
    };
  }, [
    fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs.totalCount,
    isSearched,
    loadingDataAlternativeMaterialsOs
  ]);
  const handleRefetch = () => {
    setIsSuccess(true);
    refetch();
  };

  const handleReset = () => {
    setIsSearched(false);
    getDataAlternativeMaterialsOs({
      variables: {
        ...valuesPaginationState
      }
    });
  };

  const handleSort = field => {
    resetPaginationState();
    navigate(
      AltMatListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };
  const onClose = () => {
    setVisible(false);
  };
  return (
    <>
      <Container className={classes.boxContainer}>
        <PageHeader
          className={classes.pageHeader}
          title={"Master - Alternated Material"}
        />
        <Box className={classes.filterUploadBox}>
          <AlternatedMaterialFilter
            handleSearch={handleSearch}
            message={message}
            onReset={handleReset}
            isSuccess={isSuccess}
            setIsSuccess={setIsSuccess}
          />
          <UploadFile onUploadSuccess={handleRefetch} />
        </Box>
        {(listAlternativeMaterialsOs.length > 0 ||
          loadingDataAlternativeMaterialsOs) && (
          <AlternatedMaterialTable
            data={listAlternativeMaterialsOs}
            onDeleteItem={deleteAlternativeMaterial}
            refetch={refetch}
            disabled={loadingDataAlternativeMaterialsOs || disableButton}
            pageInfo={pageInfo}
            setVisible={setVisible}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            onRowClick={() => null}
            sort={getSortParams(params)}
            onSort={handleSort}
            latestPageItemNumber={
              fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs
                .latestPageItemNumber
            }
            onNextLastPage={handleNextLastPage}
            onPreviousFirstPage={handlePreviousFirstPage}
            settings={settings}
            totalCount={
              fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs
                ?.totalCount
            }
          />
        )}
        <BottomBar
          onDownloadFile={handleDownloadFile}
          lastUpdateDate={
            fetchDataAlternativeMaterialsOs?.alternativeMaterialsOs
              .lastUpdateDate
          }
        />
      </Container>
      <DialogConfirmDelete
        onCancel={handleCloseConfirmDelete}
        onConfirm={() => null}
        open={openConfirmDelete}
        title={intl.formatMessage({
          defaultMessage: "ยืนยันลบ Alternated Material Master นี้?"
        })}
        content={intl.formatMessage({
          defaultMessage:
            "กรุณากด “ยืนยัน” เพื่อลบ Alternated Material Master หรือกด “กลับ” เพื่อกลับไป"
        })}
        cancelText={intl.formatMessage({ defaultMessage: "กลับ" })}
      />
      <DialogSuccess
        visible={visible}
        onClose={onClose}
        title={"บันทึกข้อมูล Master - Alternated Material "}
      />
    </>
  );
};

export default AlternatedMaterialView;
